import { SGA_SPEND_CONTEXT_KIND_BY_LEVER, SGA_SPEND_CONTEXT_KINDS } from './contextKinds';
import { SGA_SPEND_EVIDENCE_KEY_BY_LEVER, SGA_SPEND_EVIDENCE_KEYS } from './evidenceKeys';
import { SGA_SPEND_SCHEMA_VERSION_BY_LEVER, SGA_SPEND_SCHEMA_VERSIONS } from './schemaVersions';
import { DECISION_AGENT_SOURCE_CLASSES, type DecisionAgentSourceClass } from './sourceClasses';

export type ContractKeySpace = 'sourceClass' | 'contextKind' | 'schemaVersion' | 'evidenceKey';

declare const contractKeySpaceBrand: unique symbol;

export type BrandedContractValue<
  KeySpace extends ContractKeySpace,
  Value extends string,
> = Value & { readonly [contractKeySpaceBrand]: KeySpace };

export type ContractSourceClass = BrandedContractValue<'sourceClass', DecisionAgentSourceClass>;
export type ContractContextKind = BrandedContractValue<
  'contextKind',
  | (typeof SGA_SPEND_CONTEXT_KIND_BY_LEVER)[keyof typeof SGA_SPEND_CONTEXT_KIND_BY_LEVER]
  | (typeof SGA_SPEND_CONTEXT_KINDS)[keyof typeof SGA_SPEND_CONTEXT_KINDS]
>;
export type ContractSchemaVersion = BrandedContractValue<
  'schemaVersion',
  | (typeof SGA_SPEND_SCHEMA_VERSION_BY_LEVER)[keyof typeof SGA_SPEND_SCHEMA_VERSION_BY_LEVER]
  | (typeof SGA_SPEND_SCHEMA_VERSIONS)[keyof typeof SGA_SPEND_SCHEMA_VERSIONS]
>;
export type ContractEvidenceKey = BrandedContractValue<
  'evidenceKey',
  | (typeof SGA_SPEND_EVIDENCE_KEY_BY_LEVER)[keyof typeof SGA_SPEND_EVIDENCE_KEY_BY_LEVER]
  | (typeof SGA_SPEND_EVIDENCE_KEYS)[keyof typeof SGA_SPEND_EVIDENCE_KEYS]
>;

export type ContractAssertionContext = Readonly<{
  moduleId?: string;
  caller?: string;
}>;

const SOURCE_CLASSES = new Set<string>(Object.values(DECISION_AGENT_SOURCE_CLASSES));
const CONTEXT_KINDS = new Set<string>([
  ...Object.values(SGA_SPEND_CONTEXT_KIND_BY_LEVER),
  ...Object.values(SGA_SPEND_CONTEXT_KINDS),
]);
const SCHEMA_VERSIONS = new Set<string>([
  ...Object.values(SGA_SPEND_SCHEMA_VERSION_BY_LEVER),
  ...Object.values(SGA_SPEND_SCHEMA_VERSIONS),
]);
const EVIDENCE_KEYS = new Set<string>([
  ...Object.values(SGA_SPEND_EVIDENCE_KEY_BY_LEVER),
  ...Object.values(SGA_SPEND_EVIDENCE_KEYS),
]);

export function assertSourceClass(
  value: unknown,
  context: ContractAssertionContext = {}
): ContractSourceClass {
  return assertContractValue(
    value,
    'sourceClass',
    SOURCE_CLASSES,
    context,
    'assertSourceClass'
  ) as ContractSourceClass;
}

export function assertContextKind(
  value: unknown,
  context: ContractAssertionContext = {}
): ContractContextKind {
  return assertContractValue(
    value,
    'contextKind',
    CONTEXT_KINDS,
    context,
    'assertContextKind'
  ) as ContractContextKind;
}

export function assertSchemaVersion(
  value: unknown,
  context: ContractAssertionContext = {}
): ContractSchemaVersion {
  return assertContractValue(
    value,
    'schemaVersion',
    SCHEMA_VERSIONS,
    context,
    'assertSchemaVersion'
  ) as ContractSchemaVersion;
}

export function assertEvidenceKey(
  value: unknown,
  context: ContractAssertionContext = {}
): ContractEvidenceKey {
  return assertContractValue(
    value,
    'evidenceKey',
    EVIDENCE_KEYS,
    context,
    'assertEvidenceKey'
  ) as ContractEvidenceKey;
}

function assertContractValue(
  value: unknown,
  keySpace: ContractKeySpace,
  allowedValues: ReadonlySet<string>,
  context: ContractAssertionContext,
  defaultCaller: string
): string {
  if (typeof value === 'string' && allowedValues.has(value)) return value;

  const violation = [
    'contract.violation',
    `keySpace=${keySpace}`,
    `value=${quoteRawValue(value)}`,
    `module=${structuredToken(context.moduleId ?? 'unknown')}`,
    `caller=${structuredToken(context.caller ?? defaultCaller)}`,
  ].join(' ');
  console.error(violation);
  throw new Error(violation);
}

function quoteRawValue(value: unknown): string {
  if (typeof value === 'string') return JSON.stringify(value);
  try {
    return JSON.stringify(JSON.stringify(value) ?? String(value));
  } catch {
    return JSON.stringify(String(value));
  }
}

function structuredToken(value: string): string {
  return /^[a-zA-Z0-9._:-]+$/u.test(value) ? value : JSON.stringify(value);
}
